import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import TeacherShell, { TeacherPageHeader } from '../../../components/layout/TeacherShell';
import { Card, Button } from '../../../components/ui';
import { cn } from '../../../lib/cn';
import MonthView from './MonthView';
import WeekView from './WeekView';

const LESSON_TYPE_LABELS = {
  reading_lesson: 'Reading',
  writing_workshop: 'Writing',
  vocabulary: 'Vocabulary',
  grammar: 'Grammar',
  mixed: 'Mixed',
};

const STATUS_BADGE = {
  draft: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  planned: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  ready: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  completed: 'bg-gray-100 text-gray-600 dark:bg-gray-700/30 dark:text-gray-400',
  archived: 'bg-gray-100 text-gray-500 dark:bg-gray-700/30 dark:text-gray-500',
};

function formatDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit'
  });
}

export default function CalendarSettings({ events = [], courses = [] }) {
  const { t } = useTranslation();
  const [view, setView] = useState('month');
  const [selectedEvent, setSelectedEvent] = useState(null);

  const today = new Date();
  const [monthYear, setMonthYear] = useState({ year: today.getFullYear(), month: today.getMonth() });
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date(today);
    d.setDate(d.getDate() - d.getDay());
    d.setHours(0, 0, 0, 0);
    return d;
  });

  const handleEventClick = (event) => {
    setSelectedEvent(event);
  };

  const handleDayClick = (date) => {
    const ws = new Date(date);
    ws.setDate(ws.getDate() - ws.getDay());
    ws.setHours(0, 0, 0, 0);
    setWeekStart(ws);
    setView('week');
  };

  const handleToday = () => {
    const now = new Date();
    if (view === 'month') {
      setMonthYear({ year: now.getFullYear(), month: now.getMonth() });
    } else {
      const ws = new Date(now);
      ws.setDate(ws.getDate() - ws.getDay());
      ws.setHours(0, 0, 0, 0);
      setWeekStart(ws);
    }
  };

  return (
    <TeacherShell scrollTitle={t('teacherCalendar.title')}>
      <TeacherPageHeader
        title={t('teacherCalendar.title')}
        actions={
          <a
            href="/teacher/calendar/subscribe"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
            </svg>
            {t('teacherCalendar.subscribe')}
          </a>
        }
      />

      <div className="space-y-6">
        {/* Calendar View */}
        <Card className="p-4 md:p-6">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <Button type="button" variant="secondary" size="sm" onClick={handleToday}>
                {t('teacherCalendar.today')}
              </Button>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {events.length} {events.length !== 1 ? t('common.lessons') : t('common.lesson')} {t('common.scheduled')}
              </span>
            </div>
            <div className="flex items-center rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <button
                onClick={() => setView('month')}
                className={cn(
                  'px-3 py-1.5 text-sm font-medium transition-colors',
                  view === 'month'
                    ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                    : 'bg-white text-gray-600 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'
                )}
              >
                {t('teacherCalendar.month')}
              </button>
              <button
                onClick={() => setView('week')}
                className={cn(
                  'px-3 py-1.5 text-sm font-medium transition-colors border-l border-gray-200 dark:border-gray-700',
                  view === 'week'
                    ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                    : 'bg-white text-gray-600 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'
                )}
              >
                {t('teacherCalendar.week')}
              </button>
            </div>
          </div>

          {/* Views */}
          {view === 'month' ? (
            <MonthView
              year={monthYear.year}
              month={monthYear.month}
              events={events}
              courses={courses}
              onEventClick={handleEventClick}
              onDayClick={handleDayClick}
              onNavigate={(y, m) => setMonthYear({ year: y, month: m })}
            />
          ) : (
            <WeekView
              weekStart={weekStart}
              events={events}
              courses={courses}
              onEventClick={handleEventClick}
              onNavigate={(ws) => setWeekStart(ws)}
            />
          )}
        </Card>

        {/* Event Detail Panel */}
        {selectedEvent && (
          <Card className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  {selectedEvent.title}
                </h3>
                {selectedEvent.course_title && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                    {selectedEvent.course_title}
                  </p>
                )}
              </div>
              <button
                onClick={() => setSelectedEvent(null)}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                </svg>
                <span>{formatDateTime(selectedEvent.start)}</span>
                {selectedEvent.end && (
                  <span>– {formatDateTime(selectedEvent.end)}</span>
                )}
                <span className="text-gray-400">({selectedEvent.duration_minutes} min)</span>
              </div>

              {selectedEvent.location && (
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                  <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                  </svg>
                  <span>{selectedEvent.location}</span>
                </div>
              )}

              <div className="flex items-center gap-2 mt-1">
                <span className={cn(
                  'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                  STATUS_BADGE[selectedEvent.status] || STATUS_BADGE.active
                )}>
                  {selectedEvent.status}
                </span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                  {LESSON_TYPE_LABELS[selectedEvent.lesson_type] || selectedEvent.lesson_type}
                </span>
              </div>
            </div>

            {selectedEvent.url && (
              <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
                <a
                  href={selectedEvent.url}
                  className="text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  {t('teacherCalendar.openLesson')} &rarr;
                </a>
              </div>
            )}
          </Card>
        )}
      </div>
    </TeacherShell>
  );
}
